import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'
import * as api from '@owl-nest/api-client/latest'
import { t } from '@owl-nest/localize'

type ImpactBlockProps = {
  impact: model.impact.Impact
  lang?: api.Lang
  sdgHelpUrl: api.I18n
}

export function ImpactBlock({ impact, lang, sdgHelpUrl }: ImpactBlockProps): React.ReactElement<ImpactBlockProps> {
  const impactText = (lang && impact.impact[lang]) || model.i18n.get(impact.impact)
  const certificationsText = (lang && impact.certifications[lang]) || model.i18n.get(impact.certifications)

  return (
    <Wrapper>
      <plume.styles.heading.M>{t('Impact of your project')}</plume.styles.heading.M>
      <plume.styles.copy.M>{impactText}</plume.styles.copy.M>
      {certificationsText && (
        <>
          <plume.styles.heading.XXS>{t('Labels and certifications')}</plume.styles.heading.XXS>
          <plume.styles.copy.M>{certificationsText}</plume.styles.copy.M>
        </>
      )}
      {impact.sdgs.length !== 0 && (
        <>
          <plume.styles.heading.XXS>{t('Sustainable Development Goals')}</plume.styles.heading.XXS>
          <SDGList>
            {impact.sdgs.map((projectSDG) => {
              if (projectSDG.deleted || !projectSDG.sdg) {
                return null
              }

              const imageResource = model.i18n.get(projectSDG.sdg.logo)
              if (imageResource === undefined) {
                return null
              }

              const descriptionText = (lang && projectSDG.description[lang]) || model.i18n.get(projectSDG.description)

              return (
                <SDGItem key={projectSDG.sdg.code}>
                  <img alt="" src={model.image.getUrl(imageResource.versions, 'full')} />
                  <Description>
                    <plume.styles.heading.XXXS>{model.i18n.get(projectSDG.sdg.title)}</plume.styles.heading.XXXS>
                    <plume.styles.copy.S>{descriptionText}</plume.styles.copy.S>
                  </Description>
                </SDGItem>
              )
            })}
          </SDGList>
          <plume.styles.copy.S>
            <plume.Link kind="secondary" href={model.i18n.get(sdgHelpUrl)} target="_blank" rel="nooponer noreferrer">
              {t('Learn more on Sustainable Development Goals by the UN')}
            </plume.Link>
          </plume.styles.copy.S>
        </>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  word-break: break-word;
  max-width: 394px;

  ${plume.styles.heading.M} {
    margin-bottom: 9px;
  }

  ${plume.styles.copy.M} {
    margin-bottom: 20px;
  }

  ${plume.styles.heading.XXS} {
    margin-bottom: 20px;
  }

  ${plume.Link} {
    margin-top: 10px;
  }
`

const Description = styled.div`
  display: flex;
  flex-direction: column;
`

const SDGItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  img {
    flex: 0 0 auto;
    width: 80px;
    margin-right: 10px;
  }
`

const SDGList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;

  ${SDGItem} {
    margin-bottom: 20px;
  }
`
